import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Client Configuration
 *
 * Provides intelligent caching, background refetching, and automatic retries.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes (but can be overridden per-query)
      staleTime: 5 * 60 * 1000,

      // Retry failed requests once
      retry: 1,

      // Don't refetch on window focus (can be annoying during development)
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Refetch on mount to get fresh data
      refetchOnMount: true,

      // Renamed from cacheTime in v5
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,

      // Default error handling
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

/**
 * Query Keys Factory
 *
 * Centralized place for all query keys to avoid typos and inconsistencies.
 */
export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'],
  },

  // Quizzes
  quizzes: {
    all: ['quizzes'],
    list: (folderId, page, orgId) => ['quizzes', 'list', { folderId, page, orgId }],
    detail: (id) => ['quizzes', 'detail', id],
    deleted: ['quizzes', 'deleted'],
  },

  // Folders
  folders: {
    all: (orgId) => ['folders', { orgId }],
    detail: (id) => ['folders', 'detail', id],
  },

  // Groups
  groups: {
    all: (orgId) => ['groups', { orgId }],
    list: (page, search, orgId, campusId) => ['groups', 'list', { page, search, orgId, campusId }],
    detail: (id) => ['groups', 'detail', id],
    members: (id) => ['groups', 'members', id],
  },

  // Categories
  categories: {
    all: (orgId) => ['categories', { orgId }],
    list: (page, search, orgId, campusId) => ['categories', 'list', { page, search, orgId, campusId }],
    detail: (id) => ['categories', 'detail', id],
  },

  // Reports
  reports: {
    quiz: (quizId) => ['reports', 'quiz', quizId],
    submission: (submissionId) => ['reports', 'submission', submissionId],
  },

  // Analytics
  analytics: {
    quiz: (quizId) => ['analytics', 'quiz', quizId],
  },

  // Generated Reports
  generatedReports: {
    list: (quizId) => ['generated-reports', 'list', { quizId }],
  },
};

/**
 * Cache Invalidation Helpers
 *
 * Helper functions to invalidate related queries when data changes.
 */
export const invalidateQueries = {
  // Invalidate all quizzes when a quiz is created/updated/deleted
  quizzes: (client) => {
    client.invalidateQueries({ queryKey: queryKeys.quizzes.all });
  },

  // Invalidate specific quiz
  quiz: (client, quizId) => {
    client.invalidateQueries({ queryKey: queryKeys.quizzes.detail(quizId) });
  },

  // Invalidate all folders when a folder is created/updated/deleted
  folders: (client) => {
    client.invalidateQueries({ queryKey: ['folders'] });
  },

  // Invalidate all groups when a group is created/updated/deleted
  groups: (client) => {
    client.invalidateQueries({ queryKey: ['groups'] });
  },

  // Invalidate everything (use sparingly)
  all: (client) => {
    client.invalidateQueries();
  },
};
